import { useEffect, useState, type ReactNode } from 'react';
import {
  api,
  type CrucePerfil,
  type LogroGuardado,
  type Perfil as DatosPerfil, type FichaLogro,
  type Yo,
} from '../services/api';
import { Aviso, BotonVolver, Cargando, Tarjeta, Titulo, Vacio } from '../components/ui';
import { GraficoNoches } from '../components/GraficoNoches';
import { GraficoCompaneros } from '../components/GraficoCompaneros';
import { AyudaContextual } from '../components/AyudaContextual';
import { MINIMO_PARA_OPINAR } from '../nucleo/perfil';
import type { PeriodoPerfil } from '../nucleo/seguimiento';
import { GraficoElo } from '../components/GraficoElo';
import { LOGROS } from '../nucleo/logros';
import { fechaHora, mesEnPalabras } from '../nucleo/fechas';
import { DIAS_EN_CARRERA } from '../nucleo/ranking';
import { Marca } from '../components/Marca';

/**
 * El perfil de un jugador, con la forma de la ficha de la FIP
 * (padelfip.com/player/...): puesto gigante al lado del nombre, las dos filas
 * "este mes / total", la pareja con la que mejor le va, y solapas que llevan a
 * cada parte sin salir de la página.
 *
 * Cualquiera puede ver el de cualquiera. Tocar un compañero o un rival abre el
 * perfil de esa persona.
 */
export function Perfil({
  jugadorId,
  yo,
  onVolver,
  onVerPerfil,
}: {
  jugadorId: number;
  yo: Yo;
  onVolver: () => void;
  onVerPerfil: (id: number) => void;
}) {
  const [datos, setDatos] = useState<DatosPerfil | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDatos(null);
    setError(null);
    api
      .perfil(jugadorId)
      .then(setDatos)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error'));
    window.scrollTo(0, 0);
  }, [jugadorId]);

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <BotonVolver onClick={onVolver} />
        <Aviso tono="error">{error}</Aviso>
      </div>
    );
  }
  if (!datos) return <Cargando que="Armando el perfil" />;

  return <Ficha datos={datos} yo={yo} onVolver={onVolver} onVerPerfil={onVerPerfil} />;
}

/** Va aparte de Perfil para que los hooks de abajo corran siempre con datos. */
function Ficha({
  datos,
  yo,
  onVolver,
  onVerPerfil,
}: {
  datos: DatosPerfil;
  yo: Yo;
  onVolver: () => void;
  onVerPerfil: (id: number) => void;
}) {
  const [periodo, setPeriodo] = useState<PeriodoPerfil>('mes');
  const vista = datos.seguimiento?.[periodo] ?? { ...datos, noches: [], titulo: 'Toda la historia' };
  const { jugador: j } = datos;
  const soyYo = j.id === yo.jugador.id;
  const jugo = datos.partidos > 0;
  const porcentaje = vista.partidos ? Math.round((100 * vista.ganados) / vista.partidos) : 0;
  const diferencia = vista.games_favor - vista.games_contra;
  const socio = mejorSocio(datos);

  const solapas = [
    { id: 'resumen', texto: 'Resumen' },
    { id: 'evolucion', texto: 'Evolución' },
    ...(datos.companeros.length > 0 ? [{ id: 'resultados-parejas', texto: 'Compañeros' }] : []),
    ...(datos.companeros.length > 0 ? [{ id: 'parejas', texto: 'Parejas' }] : []),
    ...(datos.rivales.length > 0 ? [{ id: 'rivales', texto: 'Rivales' }] : []),
    { id: 'logros', texto: 'Logros' },
  ];

  const activa = useSolapaActiva(solapas.map((s) => s.id));

  return (
    <div className="flex flex-col">
      <div className="perfil-cabecera-fija hidden" aria-label="Encabezado del perfil">
        <div className="flex h-10 items-center justify-between"><Marca /><BotonVolver onClick={onVolver} /></div>
        <div className="flex h-12 items-center gap-3 rounded-xl border border-(--color-borde) bg-(--color-heroe)/40 px-3">
          <Iniciales nombre={j.nombre} chico />
          <span className="min-w-0 flex-1 text-sm font-bold"><span className="block truncate">{j.nombre}{datos.el_1.es_el_1 && ' 👑'}</span><span className="block truncate text-[10px] font-normal text-(--color-apagado)">{j.posicion ? `${j.posicion}º de ${j.de}` : 'Sin puesto'}{j.provisorio && jugo && ' · Nivel provisorio'}</span></span>
          <span className="shrink-0 text-sm text-(--color-apagado)">Nivel <b className="text-(--color-texto)">{j.elo}</b></span>
        </div>
      </div>
      {/* ── La cabecera: directo sobre el degradado, como en la FIP ── */}
      <header className="-mx-5 px-5 pt-2 pb-5">
        <div className="perfil-controles-grandes flex items-center justify-between">
          <BotonVolver onClick={onVolver} />
          <span className="text-[11px] uppercase tracking-wide text-(--color-apagado)">
            {soyYo && 'vos'}
            {soyYo && j.provisorio && jugo && ' · '}
            {j.provisorio && jugo && 'nivel provisorio'}
          </span>
        </div>

        <div className="perfil-identidad-grande mt-2 flex items-end gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-3">
              <span className="font-titulo text-7xl tabular-nums">{j.posicion ?? '–'}</span>
              <h2 className="font-titulo text-4xl break-words uppercase">{j.nombre}</h2>
            </div>
            {datos.el_1.es_el_1 && (
              <p className="mt-1 font-titulo text-2xl text-(--color-acento) uppercase">
                👑 “El 1” · {datos.el_1.campeonatos}{' '}
                {datos.el_1.campeonatos === 1 ? 'campeonato' : 'campeonatos'}
              </p>
            )}
            <div className="mt-2 inline-flex border border-white/40 bg-(--color-fondo)/50 text-xs">
              <span className="px-2.5 py-1.5 font-bold uppercase">
                {j.posicion
                  ? `${j.posicion}º de ${j.de}`
                  : `Sin puesto · no jugó en ${DIAS_EN_CARRERA} días`}
              </span>
              <span className="border-l border-white/40 px-2.5 py-1.5">
                Nivel <b className="text-sm">{j.elo}</b>
              </span>
            </div>
          </div>
          <Iniciales nombre={j.nombre} />
        </div>

        {jugo && (
          <>
            {/* ── Las dos filas, como "2026 / CAREER" en la FIP ── */}
            <div className="mt-5 bg-(--color-fondo)/55 text-center">
              {datos.mes ? (
                <FilaCifras
                  titulo={datos.mes.titulo.slice(0, 3)}
                  cifras={[
                    ['Puesto mes', datos.mes.posicion ?? '–'],
                    ['G-P', `${datos.mes.ganados}-${datos.mes.perdidos}`],
                    ['Pts del mes', datos.mes.puntos],
                  ]}
                />
              ) : (
                <p className="px-3 py-3 text-sm text-(--color-apagado)">
                  Este mes todavía no jugó.
                </p>
              )}
              <FilaCifras
                titulo="Total"
                cifras={[
                  ['Pico', datos.pico.elo],
                  ['G-P', `${datos.ganados}-${datos.perdidos}`],
                  ['Mejor racha', datos.mejor_racha],
                ]}
                conBorde
              />
            </div>
            {datos.mes && !datos.mes.elegible && (
              <p className="mt-2 text-xs text-(--color-apagado)">
                Este mes jugó pocos partidos: todavía no entra en la pelea por el campeonato.
              </p>
            )}

            {/* ── El "Paired with" de la FIP ── */}
            {socio && (
              <button
                onClick={() => onVerPerfil(socio.id)}
                className="mt-3 flex w-full items-center gap-3 bg-(--color-fondo)/55 px-3 py-2.5
                  text-left active:bg-(--color-fondo)/80"
              >
                <Iniciales nombre={socio.nombre} chico />
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block text-xs text-(--color-apagado)">Rinde más con · historia</span>
                  <span className="block truncate font-bold uppercase">{socio.nombre}</span>
                </span>
                <span className="text-right leading-tight">
                  <b className="block text-(--color-acento)">{formatoSinergia(socio.sinergia)}</b>
                  <span className="text-[11px] text-(--color-apagado)">
                    {socio.ganados}-{socio.perdidos} juntos
                  </span>
                </span>
              </button>
            )}
          </>
        )}
      </header>

      {!jugo ? (
        <Vacio>
          {soyYo ? 'Todavía no jugaste' : 'Todavía no jugó'} ningún partido. Cuando
          se cargue el primero, acá aparece la historia.
        </Vacio>
      ) : (
        <>
          {/* ── Las solapas: quedan pegadas arriba al bajar, como en la FIP ── */}
          <nav className="sticky top-0 z-10 -mx-5 flex overflow-x-auto bg-white px-3 text-(--color-fondo)">
            {solapas.map((s) => (
              <button
                key={s.id}
                onClick={() =>
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' })
                }
                className={`min-h-11 shrink-0 border-b-[3px] px-2.5 text-xs font-bold uppercase
                  ${activa === s.id ? 'border-(--color-acento)' : 'border-transparent opacity-60'}`}
              >
                {s.texto}
              </button>
            ))}
          </nav>

          <div className="flex flex-col gap-3 pt-5">
            <div className="perfil-periodos sticky top-11 z-10 -mx-5 flex flex-col gap-2 border-b border-(--color-borde) bg-(--color-panel) px-5 py-2">
              <div role="group" aria-label="Período del perfil" className="flex gap-2">
                {([['mes', 'Este mes'], ['tres_meses', '3 meses'], ['historia', 'Historia']] as const).map(([valor, texto]) =>
                  <button key={valor} type="button" aria-pressed={periodo === valor} onClick={() => setPeriodo(valor)}
                    title={valor === 'tres_meses' ? datos.seguimiento?.tres_meses.titulo ?? 'El mes actual y los dos anteriores' : undefined}
                    className={`flex min-h-11 flex-1 flex-col items-center justify-center rounded-xl border px-1 text-sm ${periodo === valor
                      ? 'border-(--color-acento) bg-(--color-acento) font-semibold text-(--color-sobre-acento)'
                      : 'border-(--color-borde) bg-(--color-panel)'}`}><span>{texto}</span>{valor === 'mes' && <span className="text-[10px] font-normal leading-tight">{datos.seguimiento?.mes.titulo ?? datos.mes?.titulo ?? 'Mes actual'}</span>}</button>)}
              </div>
            </div>
            {/* ── Cómo viene ── */}
            <Bloque id="resumen" titulo="Cómo venís">
              <Tarjeta>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm">{textoRacha(datos.racha_actual)}<span className="block text-[11px] text-(--color-apagado)">Racha actual de tu historia</span></p>
                  <div
                    className="flex gap-1.5"
                    aria-label="Últimos resultados, del más viejo al más nuevo"
                  >
                    {vista.forma.map((gano, i) => (
                      <span
                        key={i}
                        title={gano ? 'Ganó' : 'Perdió'}
                        className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold
                          ${
                            gano
                              ? 'bg-(--color-acento) text-(--color-sobre-acento)'
                              : 'border border-(--color-borde) text-(--color-apagado)'
                          }`}
                      >
                        {gano ? 'G' : 'P'}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 divide-x divide-(--color-borde) text-center">
                  <Cifra valor={vista.partidos} etiqueta="Jugados" />
                  <Cifra valor={`${porcentaje}%`} etiqueta="Efectividad" />
                  <Cifra
                    valor={diferencia > 0 ? `+${diferencia}` : diferencia}
                    etiqueta="Dif. games"
                  />
                </div>
              </Tarjeta>
            </Bloque>

            <Tarjeta>
              <h3 className="mb-3 font-titulo text-2xl">Tus noches</h3>
              <GraficoNoches key={periodo} noches={vista.noches} />
              {vista.partidos > 0 && <p className="mt-3 text-center text-sm font-semibold text-(--color-acento)">Ganaste {vista.ganados} de {vista.partidos} en este período.</p>}
            </Tarjeta>

            {/* ── La historia ── */}
            <Bloque id="evolucion" titulo="Cómo fue cambiando el nivel" ayuda={<AyudaContextual titulo="Evolución del nivel">El filtro muestra los partidos del período; el nivel no se reinicia. La línea punteada es 1000, el nivel con el que todos arrancan.</AyudaContextual>}>
              <Tarjeta>
                <GraficoElo puntos={vista.evolucion} pico={vista.pico.elo} />
                {vista.evolucion.length > 1 ? <p className="mt-3 text-sm font-semibold">{vista.evolucion[0]!.elo} → {vista.evolucion.at(-1)!.elo} · Cambio: {cambioNivel(vista.evolucion)}</p>
                  : <p className="text-sm text-(--color-apagado)">No hay partidos en este período.</p>}
                <p className="mt-1 text-xs text-(--color-apagado)">Mejor nivel histórico: {datos.pico.elo}{datos.pico.fecha && (", el " + fechaCorta(datos.pico.fecha))}.</p>
              </Tarjeta>
            </Bloque>

            <Bloque id="resultados-parejas" titulo="Con quién ganás más" ayuda={<AyudaContextual titulo="Resultados con compañeros">Resultados con {MINIMO_PARA_OPINAR} o más partidos juntos en el período. Son partidos que ya jugaron. El porcentaje indica cuántos ganaron juntos; los compañeros con menos partidos quedan en «Jugaron poco».</AyudaContextual>}>
              <Tarjeta><GraficoCompaneros companeros={vista.companeros} onVerPerfil={onVerPerfil} /></Tarjeta>
            </Bloque>

            {/* ── Con quién ── */}
            <Seccion
              id="parejas"
              titulo="Con quién rinde"
              ayuda={<AyudaContextual titulo="Qué significa vs. esp.">
                <em>vs. esp.</em> (vs. esperado) compara lo que ganaron juntos con lo que se
                esperaba de esos dos, según el nivel de los cuatro en cada partido. Así, jugar con
                el mejor del grupo no infla el número: gana el que rinde <em>más</em> de lo que
                tocaba. Con pocos partidos casi no se mueve del cero, a propósito.
              </AyudaContextual>}
              cruces={vista.companeros}
              onVerPerfil={onVerPerfil}
              columnaExtra="vs. esp."
              valorExtra={(c) => formatoSinergia(c.sinergia)}
            >
              {vista.companeros_son_ruido && (
                <Aviso>Por ahora ninguna pareja se despega: con estos partidos, es todo azar.</Aviso>
              )}
            </Seccion>

            {/* ── Contra quién ── */}
            <Seccion
              id="rivales"
              titulo="Cara a cara"
              cruces={vista.rivales}
              onVerPerfil={onVerPerfil}
              columnaExtra="%"
              valorExtra={(c) => `${Math.round((100 * c.ganados) / c.partidos)}%`}
            />

            {/* ── Logros ── */}
            <Bloque id="logros" titulo="Logros">
              <Logros datos={datos} />
            </Bloque>
          </div>
        </>
      )}

    </div>
  );
}

/** En la FIP va la foto. Acá no hay fotos: van las iniciales, en el celeste. */
function Iniciales({ nombre, chico }: { nombre: string; chico?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full bg-(--color-acento)
        font-titulo text-(--color-sobre-acento)
        ${chico ? 'h-9 w-9 text-lg' : 'h-20 w-20 text-4xl'}`}
    >
      {iniciales(nombre)}
    </span>
  );
}

function FilaCifras({
  titulo,
  cifras,
  conBorde,
}: {
  titulo: string;
  cifras: [string, string | number][];
  conBorde?: boolean;
}) {
  return (
    <div
      className={`grid grid-cols-[3.5rem_repeat(3,minmax(0,1fr))] items-center
        ${conBorde ? 'border-t border-white/10' : ''}`}
    >
      <span className="border-r border-white/25 py-3 font-titulo text-xl uppercase">{titulo}</span>
      {cifras.map(([etiqueta, valor]) => (
        <span key={etiqueta} className="px-1 py-2">
          <span className="block text-[10px] uppercase tracking-wide text-(--color-apagado)">
            {etiqueta}
          </span>
          <span className="block text-base font-bold tabular-nums">{valor}</span>
        </span>
      ))}
    </div>
  );
}

function Cifra({ valor, etiqueta }: { valor: string | number; etiqueta: string }) {
  return (
    <div>
      <div className="font-titulo text-3xl tabular-nums">{valor}</div>
      <div className="text-[10px] uppercase tracking-wide text-(--color-apagado)">{etiqueta}</div>
    </div>
  );
}

/**
 * Todo el catálogo, con los conseguidos arriba y a color. Los que faltan se
 * ven igual, apagados: saber qué se puede ganar es la mitad de la gracia.
 */
function Logros({ datos }: { datos: DatosPerfil }) {
  const conseguidos = new Map<string, LogroGuardado[]>();
  for (const l of datos.logros) {
    conseguidos.set(l.logro_id, [...(conseguidos.get(l.logro_id) ?? []), l]);
  }
  const catalogo: FichaLogro[] = datos.logros_catalogo ?? LOGROS.map(l => ({ ...l, humor: Boolean(l.humor), origen: 'oficial' as const, archivado: false }));
  const disponibles = catalogo.filter(l => !l.archivado);
  const visibles = catalogo.filter(l => !l.archivado || conseguidos.has(l.id));
  const serios = visibles.filter((l) => !l.humor);
  const deHumor = visibles.filter((l) => l.humor);
  const ordenar = (lista: readonly FichaLogro[]) =>
    [...lista].sort((a, b) => Number(conseguidos.has(b.id)) - Number(conseguidos.has(a.id)));
  const { reinados } = datos.el_1;

  return (
    <details className="group rounded-2xl border border-(--color-borde) bg-(--color-tarjeta)">
      <summary className="flex min-h-20 cursor-pointer list-none items-center gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
        <span className="font-titulo text-4xl tabular-nums text-(--color-acento)">{disponibles.filter(l => conseguidos.has(l.id)).length}<span className="text-2xl text-(--color-apagado)">/{disponibles.length}</span></span>
        <span className="flex-1 text-sm font-semibold">Conseguidos<span className="block text-xs font-normal text-(--color-apagado)">Toda tu historia · ver logros</span></span>
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-(--color-acento) transition-transform group-open:rotate-180"><path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
      </summary>
      <div className="flex flex-col gap-3 border-t border-(--color-borde) p-3">

      {/* El 1 es un título que rota, no un logro: queda el historial de cuándo lo tuvo. */}
      {reinados.length > 0 && (
        <div className="rounded-xl border border-(--color-acento) bg-(--color-tarjeta) px-3 py-2">
          <span className="block text-sm font-semibold">
            👑 “El 1”{datos.el_1.es_el_1 && <span className="text-(--color-acento)"> · ahora</span>}
          </span>
          <span className="block text-xs text-(--color-apagado)">
            El que más campeonatos del mes tiene. Rota: cuando otro lo supera, pasa a él.
          </span>
          <ul className="mt-1 text-[11px] text-(--color-acento)">
            {reinados.map((r) => (
              <li key={r.desde}>
                {r.hasta
                  ? `de ${mesEnPalabras(r.desde)} a ${mesEnPalabras(r.hasta)}`
                  : `desde ${mesEnPalabras(r.desde)}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      <GrillaLogros logros={ordenar(serios)} conseguidos={conseguidos} />

      <Titulo chico>Los otros logros 😅</Titulo>
      <GrillaLogros logros={ordenar(deHumor)} conseguidos={conseguidos} />
      </div>
    </details>
  );
}

function GrillaLogros({
  logros,
  conseguidos,
}: {
  logros: readonly FichaLogro[];
  conseguidos: Map<string, LogroGuardado[]>;
}) {
  return (
    <ul className="grid grid-cols-2 gap-2">
      {logros.map((logro) => {
        const suyos = conseguidos.get(logro.id);
        const repetible = logro.contexto === 'noche' || logro.contexto === 'partido';
        return (
          <li
            key={logro.id}
            className={`rounded-xl border px-3 py-2
              ${suyos ? 'border-(--color-acento) bg-(--color-tarjeta)' : 'border-(--color-borde) opacity-45'}`}
          >
            <span className={`block text-2xl ${suyos ? '' : 'grayscale'}`} aria-hidden>
              {suyos && logro.contexto === 'nivel' ? medalla(suyos) : logro.emoji}
            </span>
            <span className="block text-sm font-semibold leading-tight">
              {logro.nombre}
              {suyos && repetible && suyos.length > 1 && (
                <span className="text-(--color-acento)"> ×{suyos.length}</span>
              )}
            </span>
            <span className="block text-xs text-(--color-apagado)">{logro.descripcion}</span>
            {logro.origen !== 'oficial' && <span className="block text-[10px] uppercase tracking-wide text-(--color-apagado)">{logro.origen === 'manual_grupo' ? 'Del administrador' : 'Del grupo'}</span>}
            {suyos?.[0]?.motivo && <span className="block text-[11px] text-(--color-acento)">{suyos[0].motivo}</span>}
            {suyos && (
              <span className="mt-0.5 block text-[11px] text-(--color-acento)">
                {detalle(logro, suyos)}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Rey del tiebreak: la medalla del nivel más alto al que llegó. */
function medalla(suyos: LogroGuardado[]): string {
  const alto = Math.max(...suyos.map((s) => Number(s.contexto)));
  return alto >= 20 ? '🥇' : alto >= 10 ? '🥈' : '🥉';
}

/** Lo que va debajo de un logro conseguido: cuándo, con quién, a quién, qué mes. */
function detalle(logro: FichaLogro, suyos: LogroGuardado[]): string {
  const hasta3 = (lista: string[]) =>
    lista.length > 3 ? `${lista.slice(0, 3).join(', ')} y ${lista.length - 3} más` : lista.join(', ');
  const nombres = () => hasta3(suyos.map((s) => s.contexto_nombre ?? '¿?'));
  switch (logro.contexto) {
    case 'ninguno':
      return `el ${fechaHora(suyos[0]!.obtenido_en).dia}`;
    case 'noche':
    case 'partido':
      // Lo más reciente primero.
      return hasta3([...suyos].sort((a, b) => b.obtenido_en - a.obtenido_en).map((s) => fechaHora(s.obtenido_en).dia));
    case 'mes':
      return hasta3(suyos.map((s) => mesEnPalabras(s.contexto)));
    case 'anio':
      return suyos.map((s) => s.contexto).join(', ');
    case 'companero':
      return `con ${nombres()}`;
    case 'rival':
      return `de ${nombres()}`;
    case 'nivel': {
      const alto = Math.max(...suyos.map((s) => Number(s.contexto)));
      return `${alto} tiebreaks ganados`;
    }
  }
}

/** Una parte del perfil, con el ancla a la que salta su solapa. */
function Bloque({ id, titulo, children, ayuda }: { id: string; titulo: string; children: ReactNode; ayuda?: ReactNode }) {
  return (
    // scroll-mt: que al saltar, el título no quede tapado por las solapas pegadas.
    <section id={id} className="flex scroll-mt-36 flex-col gap-3">
      <div className="flex min-h-11 items-center justify-between gap-2"><Titulo>{titulo}</Titulo>{ayuda}</div>
      {children}
    </section>
  );
}

/**
 * Una tabla de cruces (compañeros o rivales). Los de poca muestra van plegados
 * abajo: existen y se pueden ver, pero no encabezan nada.
 */
function Seccion({
  id,
  titulo,
  cruces,
  onVerPerfil,
  columnaExtra,
  valorExtra,
  children,
  ayuda,
}: {
  id: string;
  titulo: string;
  cruces: CrucePerfil[];
  onVerPerfil: (id: number) => void;
  columnaExtra: string;
  valorExtra: (c: CrucePerfil) => string;
  children?: ReactNode;
  ayuda?: ReactNode;
}) {
  if (cruces.length === 0) return null;

  const conMuestra = cruces.filter((c) => !c.poca_muestra);
  const pocos = cruces.filter((c) => c.poca_muestra);

  const tabla = (filas: CrucePerfil[], atenuada: boolean) => (
    <table className={`w-full text-sm ${atenuada ? 'opacity-60' : ''}`}>
      <thead>
        <tr className="bg-(--color-heroe) text-[11px] uppercase tracking-wide">
          <th className="py-2 pl-3 text-left font-normal">Jugador</th>
          <th className="py-2 text-right font-normal">PJ</th>
          <th className="py-2 text-right font-normal">G-P</th>
          <th className="py-2 pr-3 text-right font-normal">{columnaExtra}</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((c) => (
          <tr
            key={c.id}
            onClick={() => onVerPerfil(c.id)}
            className="cursor-pointer border-b border-(--color-borde)/40 last:border-0 active:bg-white/5"
          >
            <td className="py-3 pl-3 underline decoration-(--color-borde) underline-offset-2">
              {c.nombre}
            </td>
            <td className="py-3 text-right tabular-nums text-(--color-apagado)">{c.partidos}</td>
            <td className="py-3 text-right tabular-nums text-(--color-apagado)">
              {c.ganados}-{c.perdidos}
            </td>
            <td className="py-3 pr-3 text-right font-semibold tabular-nums">{valorExtra(c)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <Bloque id={id} titulo={titulo} ayuda={ayuda}>
      {conMuestra.length > 0 ? (
        <Tarjeta sinRelleno>{tabla(conMuestra, false)}</Tarjeta>
      ) : (
        <p className="text-sm text-(--color-apagado)">
          Todavía no hay nadie con suficientes partidos para decir algo.
        </p>
      )}

      {pocos.length > 0 && (
        <details className="overflow-hidden rounded-2xl border border-(--color-borde) bg-(--color-tarjeta)">
          <summary className="cursor-pointer px-4 py-3 text-sm text-(--color-apagado)">
            Jugaron poco ({pocos.length})
          </summary>
          {tabla(pocos, true)}
        </details>
      )}

      {children}
    </Bloque>
  );
}

/**
 * Qué solapa marcar: la de la última sección cuyo título ya pasó por debajo de
 * las solapas. Se recalcula al scrollear, así la marca acompaña al dedo y no
 * se queda en la que se tocó.
 */
function useSolapaActiva(ids: string[]): string {
  const [activa, setActiva] = useState(ids[0] ?? '');
  const clave = ids.join(',');
  useEffect(() => {
    const lista = clave.split(',');
    let pedido = 0;
    const medir = () => {
      pedido = 0;
      let actual = lista[0] ?? '';
      for (const id of lista) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 224) actual = id;
      }
      // Al fondo de la página la última sección puede no llegar arriba nunca.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
        actual = lista[lista.length - 1] ?? actual;
      }
      setActiva(actual);
    };
    const alScrollear = () => {
      if (!pedido) pedido = requestAnimationFrame(medir);
    };
    window.addEventListener('scroll', alScrollear, { passive: true });
    return () => {
      window.removeEventListener('scroll', alScrollear);
      if (pedido) cancelAnimationFrame(pedido);
    };
  }, [clave]);
  return activa;
}

/**
 * La pareja que se muestra arriba, como el "Paired with" de la FIP. Solo si el
 * número dice algo: con poca muestra, o si todas las parejas son ruido, no se
 * destaca a nadie — sería inventar una pareja.
 */
function mejorSocio(datos: DatosPerfil): CrucePerfil | null {
  if (datos.companeros_son_ruido) return null;
  const candidatos = datos.companeros.filter((c) => !c.poca_muestra && c.sinergia > 0);
  if (candidatos.length === 0) return null;
  return candidatos.reduce((a, b) => (b.sinergia > a.sinergia ? b : a));
}

/** "Juan Pérez" -> "JP" · "Lucho" -> "LU" */
function iniciales(nombre: string): string {
  const palabras = nombre.trim().split(/\s+/).filter(Boolean);
  if (palabras.length >= 2) return (palabras[0]![0]! + palabras[1]![0]!).toUpperCase();
  return (palabras[0] ?? '?').slice(0, 2).toUpperCase();
}

function textoRacha(racha: number): string {
  if (racha >= 2) return `Viene de ${racha} ganados seguidos.`;
  if (racha === 1) return 'Ganó el último.';
  if (racha === -1) return 'Perdió el último.';
  if (racha <= -2) return `Viene de ${-racha} perdidos seguidos.`;
  return '';
}

/** +0.114 -> "+0,11" · se muestra con dos decimales, que es lo que se puede leer. */
function formatoSinergia(valor: number): string {
  const texto = Math.abs(valor).toFixed(2).replace('.', ',');
  if (Math.abs(valor) < 0.005) return '0';
  return `${valor > 0 ? '+' : '−'}${texto}`;
}

function fechaCorta(fecha: string): string {
  const [, mes, dia] = fecha.split('-') as [string, string, string];
  return `${Number(dia)}/${Number(mes)}`;
}

function cambioNivel(puntos: { elo: number }[]) { const cambio = puntos.at(-1)!.elo - puntos[0]!.elo; return cambio > 0 ? `+${cambio}` : String(cambio); }
